import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { EpidemiologyError, PatientRequestsService } from '@app/core/patient-requests';
import { formatDateAsYyyyMmDdLocal } from '@app/core/utils/format-local-iso-date';

/** Panel P.3.1 — rango de fechas y apertura de la vista 3.2 en pestaña nueva (SIS005 / DSI-003). */
@Component({
  selector: 'afoone-epidemiology-search-panel',
  templateUrl: './epidemiology-search-panel.component.html',
  styleUrls: ['./epidemiology-search-panel.component.css'],
})
export class EpidemiologySearchPanelComponent implements OnInit {
  /** NHC / historia del paciente (trim en API). */
  @Input() patientId = '';
  /** Código laboratorio (tLabNr). */
  @Input() labNr = 0;
  /** Centro Sisifo (opcional). */
  @Input() centerId: number | null = null;
  /** Primeros 3 caracteres del laboratorio para enlaces a ver petición (p. ej. MIC). */
  @Input() detailLaboratorio = 'MIC';

  readonly minEpidemiologyDate = new Date(1900, 0, 1);

  dateFrom = new Date();
  dateTo = new Date();
  /** Límite superior dinámico del calendario “Hasta” (hoy, fin de día no necesario para comparación de fechas). */
  maxDateTo = new Date();
  loading = false;
  panelMessage: string | null = null;
  private lastValidDateFrom = new Date();
  private lastValidDateTo = new Date();

  constructor(
    private router: Router,
    private service: PatientRequestsService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const y = new Date().getFullYear();
    this.dateFrom = new Date(y, 0, 1);
    this.lastValidDateFrom = new Date(this.dateFrom);
    const t = new Date();
    this.dateTo = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    this.lastValidDateTo = new Date(this.dateTo);
    this.maxDateTo = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    this.clampDates();
  }

  get dateFromMaxDate(): Date {
    return this.isValidDate(this.dateTo) ? this.dateTo : this.maxDateTo;
  }

  get dateToMinDate(): Date {
    return this.isValidDate(this.dateFrom) ? this.dateFrom : this.minEpidemiologyDate;
  }

  /**
   * Calendario "Desde": no enlazar `[maxDate]` a un getter con `new Date()` cada CD — PrimeNG reinicia el panel
   * y bloquea navegación (mes/año atrás). Aquí `maxDate` = hoy; el cruce desde/hasta se ajusta en código.
   */
  onDateFromChange(value: Date | null): void {
    if (!this.isValidDate(value)) {
      this.dateFrom = new Date(this.lastValidDateFrom);
      return;
    }
    const cap = this.stripTime(this.dateFromMaxDate);
    let next = this.stripTime(value);
    if (next > cap) {
      next = new Date(cap);
    }
    this.dateFrom = next;
    this.lastValidDateFrom = new Date(next);
    if (this.dateFrom > this.dateTo) {
      this.dateTo = new Date(this.dateFrom);
      this.lastValidDateTo = new Date(this.dateTo);
    }
  }

  /** Calendario "Hasta": no puede ser anterior a `dateFrom` ni posterior a hoy. */
  onDateToChange(value: Date | null): void {
    if (!this.isValidDate(value)) {
      this.dateTo = new Date(this.lastValidDateTo);
      return;
    }
    let next = this.stripTime(value);
    if (next > this.stripTime(this.maxDateTo)) {
      next = this.stripTime(this.maxDateTo);
    }
    if (next < this.stripTime(this.dateFrom)) {
      this.dateFrom = new Date(next);
      this.lastValidDateFrom = new Date(this.dateFrom);
    }
    this.dateTo = next;
    this.lastValidDateTo = new Date(next);
  }

  openEpidemiologyView(): void {
    const pid = String(this.patientId || '').trim();
    this.panelMessage = null;
    if (!pid || !this.labNr || !this.isValidDate(this.dateFrom) || !this.isValidDate(this.dateTo)) return;

    const dateFrom = formatDateAsYyyyMmDdLocal(this.dateFrom);
    const dateTo = formatDateAsYyyyMmDdLocal(this.dateTo);
    const q: Record<string, string | number> = {
      patientId: pid,
      labNr: Number(this.labNr),
      dateFrom,
      dateTo,
    };
    const c = this.centerId;
    if (c != null && !Number.isNaN(Number(c))) {
      q['centroId'] = Number(c);
    }
    const lab = String(this.detailLaboratorio || '').trim().slice(0, 3).toUpperCase();
    if (lab) {
      q['detailLaboratorio'] = lab;
    }

    this.loading = true;
    this.service.searchEpidemiology({
      patientId: pid,
      labNr: Number(this.labNr),
      dateFrom,
      dateTo,
      centroId: c != null && !Number.isNaN(Number(c)) ? Number(c) : undefined,
    }).subscribe({
      next: (res) => {
        this.loading = false;
        if (!Array.isArray(res.data) || res.data.length === 0) {
          this.panelMessage = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.EPIDEMIOLOGY_NOT_FOUND');
          return;
        }
        this.openEpidemiologyTab(q);
      },
      error: (err: unknown) => {
        this.loading = false;
        if (err instanceof EpidemiologyError && err.msgCode === 'EPIDEMIOLOGY_NOT_FOUND') {
          this.panelMessage = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.EPIDEMIOLOGY_NOT_FOUND');
          return;
        }
        if (err instanceof EpidemiologyError && err.msgCode) {
          this.panelMessage = this.translate.instant(`WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}`);
          return;
        }
        this.panelMessage = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.DB_ERROR');
      },
    });
  }

  private openEpidemiologyTab(q: Record<string, string | number>): void {
    const tree = this.router.createUrlTree(['/patient-requests', 'epidemiology-view'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const origin = typeof globalThis !== 'undefined' && globalThis.location?.origin ? globalThis.location.origin : '';
    const full = origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open?.(full, '_blank', 'noopener,noreferrer');
  }

  private clampDates(): void {
    if (this.dateFrom > this.dateTo) {
      this.dateTo = new Date(this.dateFrom);
      this.lastValidDateTo = new Date(this.dateTo);
    }
    if (this.dateTo > this.maxDateTo) {
      this.dateTo = this.stripTime(this.maxDateTo);
      this.lastValidDateTo = new Date(this.dateTo);
    }
    if (this.dateFrom > this.maxDateTo) {
      this.dateFrom = this.stripTime(this.maxDateTo);
      this.lastValidDateFrom = new Date(this.dateFrom);
    }
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private isValidDate(value: Date | null): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }

}
